// Implements: F-PRV-002, F-PRV-017, F-SHR-013
/**
 * The address of the frame document of one version (design 5.1, ADR-0007).
 *
 * The viewer loads the document by `src` from this authenticated, same-origin route. The type is
 * branded and only `previewDocumentPath` produces it, so `SandboxedFrame` cannot be handed an
 * arbitrary string by accident; the atom also re-checks the shape at run time, because a brand is
 * erased by the compiler and a cast is one keyword away.
 *
 * The only queries the frame shape accepts are `?artifact=diagram`, written by
 * `previewDiagramDocumentPath` (the version's journey diagram, F-PRV-017), and
 * `?artifact=diagram_walkthrough`, written by `previewWalkthroughDocumentPath` (its screen
 * walkthrough, mockup v3 P6 C6.4). Any other query is refused.
 *
 * Mockup v3 P8 (F-SHR-013): the visitor preview (`GET /mockups/{id}/share-preview`, C6) is the
 * second shape, built only by `sharePreviewDocumentPath`: `artifact` (`prototype` or `diagram`) and,
 * for a fixed link, one encoded `version_id`. It carries no share token, so it never counts a visit.
 * Owner decision 2026-10-19: one listed link is previewed by its id alone, `?link_id=<uuid>`, built
 * only by `shareLinkPreviewDocumentPath`; the shape admits exactly one canonical UUID and nothing
 * beside it (no other parameter, no encoded separator, no other route).
 */
/** Where the API is served (`shared/api/apiBase.ts`); `shared/ui` may not import it, so it is repeated and tested. */
const API_BASE = "/api";

export type FrameDocumentPath = string & { readonly __frameDocumentPath: true };

/** One path segment: never empty, never a dot segment (also written `%2e`), never a separator. */
const SEGMENT = String.raw`(?!(?:\.|%2e){1,2}(?:/|$))[^/?#\\]+`;
// eslint-disable-next-line security/detect-non-literal-regexp -- assembled only from the module constants above; no runtime input reaches the pattern.
const SHAPE = new RegExp(
  `^${API_BASE}/mockups/${SEGMENT}/preview/${SEGMENT}/document(?:[?]artifact=diagram(?:_walkthrough)?)?$`,
  "i",
);

/** One encoded query value: what `encodeURIComponent` writes, never empty, never `&`, `=` or `#`. */
const QUERY_VALUE = String.raw`[A-Za-z0-9%._~!*'()-]+`;
// eslint-disable-next-line security/detect-non-literal-regexp -- assembled only from the module constants above; no runtime input reaches the pattern.
const SHARE_PREVIEW_SHAPE = new RegExp(
  `^${API_BASE}/mockups/${SEGMENT}/share-preview[?]artifact=(?:prototype|diagram)(?:&version_id=${QUERY_VALUE})?$`,
  "i",
);

/** A link id as the server issues it: one canonical UUID; its hexadecimal digits in either case. */
const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
/** `SEGMENT` for a case-sensitive pattern: the encoded dot is refused in either case. */
const CASED_SEGMENT = String.raw`(?!(?:\.|%2[eE]){1,2}(?:/|$))[^/?#\\]+`;
/**
 * One listed link (owner 2026-10-19): case-sensitive, so the route and the `link_id` key are
 * exactly as written (verifier finding: `?LINK_ID=` is refused); only the id's hex may vary.
 */
// eslint-disable-next-line security/detect-non-literal-regexp -- assembled only from the module constants above; no runtime input reaches the pattern.
const SHARE_LINK_PREVIEW_SHAPE = new RegExp(`^${API_BASE}/mockups/${CASED_SEGMENT}/share-preview[?]link_id=${UUID}$`);

export function previewDocumentPath(mockupId: string, versionId: string): FrameDocumentPath {
  const path = `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/preview/${encodeURIComponent(versionId)}/document`;
  return path as FrameDocumentPath;
}

/** The same frame route, framing the version's Mermaid diagram instead of the prototype (F-PRV-017). */
export function previewDiagramDocumentPath(mockupId: string, versionId: string): FrameDocumentPath {
  return `${previewDocumentPath(mockupId, versionId)}?artifact=diagram` as FrameDocumentPath;
}

/** The same frame route, framing the version's stored screen walkthrough (P6, C6.4). */
export function previewWalkthroughDocumentPath(mockupId: string, versionId: string): FrameDocumentPath {
  return `${previewDocumentPath(mockupId, versionId)}?artifact=diagram_walkthrough` as FrameDocumentPath;
}

/** The attachment of the same version: the self-contained document, without the frame bootstrap. */
export function previewDownloadPath(mockupId: string, versionId: string): string {
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/preview/${encodeURIComponent(versionId)}/document.html`;
}

/**
 * Mockup v3 P8: the visitor preview of a share, the very frame its link serves for that artifact and
 * version (`null`: the mockup's current version, what a live link shows), read under the session.
 */
export function sharePreviewDocumentPath(
  mockupId: string,
  artifact: "prototype" | "diagram",
  versionId: string | null,
): FrameDocumentPath {
  const version = versionId === null ? "" : `&version_id=${encodeURIComponent(versionId)}`;
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/share-preview?artifact=${artifact}${version}` as FrameDocumentPath;
}

/**
 * Owner decision 2026-10-19: the visitor preview of one listed link, exactly what that link serves,
 * for anyone who sees it. A link id that is not a UUID is encoded, and so refused by the shape.
 */
export function shareLinkPreviewDocumentPath(mockupId: string, linkId: string): FrameDocumentPath {
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/share-preview?link_id=${encodeURIComponent(linkId)}` as FrameDocumentPath;
}

export function isFrameDocumentPath(value: string): value is FrameDocumentPath {
  return SHAPE.test(value) || SHARE_PREVIEW_SHAPE.test(value) || SHARE_LINK_PREVIEW_SHAPE.test(value);
}
